
import React, { useState, useEffect, useMemo, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getCandidateFeedback, createFeedback, updateFeedback, deleteFeedback, getCandidates, getJobs, applyForJob, getCandidateJobApplications, updateJobApplicationStatus, withdrawApplication, notifyJobApplication } from "../api";
import { AuthContext } from "../AuthContext";
import PopupMessage from "./PopupMessage";
import { feedbackStatusOptions } from "../services/dropdownOptions";
import { APPLIED_JOBS_TABLE_COLUMNS, FEEDBACK_TABLE_COLUMNS } from "../services/commonConstants";

import {
  candidateStatusOptions,
  jobStatusOptions,
  resourceStatusOptions,
  rejectionReasons
} from "../services/dropdownOptions";

const FeedbackPage = () => {
  const { candidateId } = useParams(); // URL param is 'candidateId'
  const navigate = useNavigate();
  const location = useLocation();
  const candidateData = location.state?.candidate;
  const { user } = useContext(AuthContext); // Get logged-in user

  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'CreatedDate', direction: 'desc' });
  const [fullCandidateData, setFullCandidateData] = useState(null);

  const getValidResumeUrl = (resumeUrl) => {
    if (!resumeUrl || typeof resumeUrl !== 'string') return '';
    const trimmed = resumeUrl.trim();
    if (!trimmed) return '';
    try {
      const parsed = new URL(trimmed);
      return /^https?:$/.test(parsed.protocol) ? trimmed : '';
    } catch (_error) {
      return '';
    }
  };
  const [showCandidateDetails, setShowCandidateDetails] = useState(false);
  const [jobApplicationFilter, setJobApplicationFilter] = useState('');
  const [jobApplicationStatus, setJobApplicationStatus] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    comment: "",
    status: "HR Round"
  });
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Edit state
  const [editingFeedbackId, setEditingFeedbackId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    comment: "",
    status: ""
  });

  // Job Applications state
  const [jobApplications, setJobApplications] = useState([]);
  const [showApplyJobPopup, setShowApplyJobPopup] = useState(false);
  const [jobSearch, setJobSearch] = useState("");
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [applyingForJob, setApplyingForJob] = useState(false);

  // Notification popup after applying
  const [showNotificationPopup, setShowNotificationPopup] = useState(false);
  const [appliedJobDetails, setAppliedJobDetails] = useState(null);

  // Already applied popup
  const [showAlreadyAppliedPopup, setShowAlreadyAppliedPopup] = useState(false);
  const [alreadyAppliedJobName, setAlreadyAppliedJobName] = useState('');

  // Fetch feedback and candidate data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('🚀 FeedbackPage: Starting fetch data for candidate', candidateId);
        setLoading(true);

        // Fetch feedback
        const feedbackResponse = await getCandidateFeedback(candidateId);
        console.log('📥 FeedbackPage: Feedback response received:', feedbackResponse);

        // Fetch candidates to get full candidate data
        const candidatesResponse = await getCandidates();
        console.log('📥 FeedbackPage: Candidates response received:', candidatesResponse);

        if (feedbackResponse.success && candidatesResponse.success) {
          console.log('✅ FeedbackPage: Success - Data loaded');
          setFeedback(feedbackResponse.data || []);

          // Find the specific candidate
          const candidate = candidatesResponse.data.find(c => c.CandidateID == candidateId);
          if (candidate) {
            // Transform data similar to CandidatePage
            const normalizeDate = (value) => {
              if (!value) return null;
              const date = new Date(value);
              return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
            };

            const transformedCandidate = {
              id: candidate.CandidateID,
              name: candidate.FullName,
              email: candidate.Email,
              phone: candidate.Mobile,
              location: candidate.CurrentLocation,
              role: candidate.Role,
              readyToTravel: candidate.ReadyToTravel === 1 || candidate.ReadyToTravel === '1' || candidate.ReadyToTravel === true ? 1 : 0,
              remoteAvailable: candidate.RemoteAvailable === 1 || candidate.RemoteAvailable === '1' || candidate.RemoteAvailable === true ? 1 : 0,
              experience: candidate.YearsOfExperience != null ? candidate.YearsOfExperience : 'Not specified',
              primarySkills: candidate.PrimarySkills || 'Not specified',
              secondarySkills: candidate.SecondarySkills || 'Not specified',
              techStack: candidate.TechStack || 'Not specified',
              skills: candidate.Skills || [candidate.PrimarySkills, candidate.SecondarySkills, candidate.TechStack]
                .filter(Boolean)
                .join(', '),
              workType: candidate.WorkType || 'Not specified',
              noticePeriod: candidate.NoticePeriod || candidate.noticePeriod || 'Not specified',
              c2c: candidate.CTC,
              expectedCTC: candidate.ExpectedCTC || 'Not specified',
              freelanceRate: candidate.FreelanceRate || 'Not specified',
              referrerCode: candidate.ReferrerCode || 'Not specified',
              source: candidate.Source || 'Not specified',
              status: candidate.Status,
              aadharNo: candidate.AadharNo || 'Not specified',
              pancardNo: candidate.PancardNo || 'Not specified',
              appliedDate: candidate.CreatedAt,
              resumeFile: candidate.OriginalFileName,
              resumeUrl: getValidResumeUrl(candidate.StoredPath),
              resumeId: candidate.ResumeID,
              feedbackStatus: candidate.FeedbackStatus || 'No feedback',
              feedbackComment: candidate.FeedbackComment || 'No feedback',
              availableFrom: normalizeDate(candidate.AvailableFrom ?? candidate.availableFrom)
            };
            setFullCandidateData(transformedCandidate);
          }
        } else {
          const errorMsg = feedbackResponse.message || candidatesResponse.message || 'Failed to load data';
          setError(errorMsg);
          setShowErrorPopup(true);
        }
      } catch (err) {
        console.error('💥 FeedbackPage: Error fetching data:', err);
        setError('Network error while loading data');
        setShowErrorPopup(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [candidateId]);

  // Fetch job applications
  useEffect(() => {
    const fetchJobApps = async () => {
      try {
        console.log('🚀 FeedbackPage: Fetching job applications for candidate', candidateId);
        const response = await getCandidateJobApplications(candidateId);
        if (response.success) {
          console.log('✅ FeedbackPage: Job applications fetched:', response.data);
          setJobApplications(response.data || []);
        } else {
          console.log('ℹ️ FeedbackPage: No job applications found');
          setJobApplications([]);
        }
      } catch (err) {
        console.error('💥 FeedbackPage: Error fetching job applications:', err);
        setJobApplications([]);
      }
    };

    if (candidateId) {
      fetchJobApps();
    }
  }, [candidateId]);

  useEffect(() => {
    const fetchJobsList = async () => {
      try {
        setJobsLoading(true);
        const response = await getJobs();
        if (response.success) {
          setJobs(response.data || []);
        } else {
          setJobs([]);
        }
      } catch (err) {
        console.error('💥 FeedbackPage: Error fetching jobs list:', err);
        setJobs([]);
      } finally {
        setJobsLoading(false);
      }
    };

    fetchJobsList();
  }, []);

  useEffect(() => {
    if (!showApplyJobPopup) {
      setJobSearch("");
      setSelectedJobId(null);
    }
  }, [showApplyJobPopup]);

  // Filter feedback
  const filteredFeedback = useMemo(() => {
    let filtered = feedback.filter(item => {
      const matchesStatus = !statusFilter || item.Status === statusFilter;

      let matchesDate = true;
      if (startDate || endDate) {
        const itemDate = item.CreatedDate ? new Date(item.CreatedDate).toISOString().split('T')[0] : '';
        if (startDate && itemDate < startDate) matchesDate = false;
        if (endDate && itemDate > endDate) matchesDate = false;
      }

      return matchesStatus && matchesDate;
    });

    // Sort feedback
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [feedback, statusFilter, startDate, endDate, sortConfig]);

  // Get unique statuses
  const uniqueStatuses = [...new Set(feedback.map(item => item.Status).filter(Boolean))];

  const filteredJobApplications = useMemo(() => {
    const query = jobApplicationFilter.trim().toLowerCase();
    return jobApplications.filter((job) => {
      const matchesSearch = !query || [
        String(job.job_id),
        job.job_title,
        job.job_company,
        job.job_location,
        job.job_type
      ].some((value) => String(value || '').toLowerCase().includes(query));

      const matchesStatus = !jobApplicationStatus || String(job.status || '').toLowerCase() === jobApplicationStatus.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [jobApplications, jobApplicationFilter, jobApplicationStatus]);

  const filteredJobs = useMemo(() => {
    const query = jobSearch.trim().toLowerCase();
    if (!query) {
      return jobs;
    }
    return jobs.filter((job) => {
      const idMatch = job.job_id !== undefined && String(job.job_id).includes(query);
      const titleMatch = job.title && job.title.toLowerCase().includes(query);
      return idMatch || titleMatch;
    });
  }, [jobs, jobSearch]);

  // Handle form input change
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle delete feedback
  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) {
      return;
    }

    try {
      console.log('🗑️ FeedbackPage: Deleting feedback:', feedbackId);
      const response = await deleteFeedback(feedbackId);
      console.log('📥 FeedbackPage: Delete response:', response);

      if (response.success) {
        console.log('✅ FeedbackPage: Feedback deleted successfully');
        setSuccessMessage('Feedback deleted successfully! 🗑️');
        setShowSuccessPopup(true);

        // Refresh feedback list
        const updatedResponse = await getCandidateFeedback(candidateId);
        if (updatedResponse.success) {
          setFeedback(updatedResponse.data || []);
        }
      } else {
        setError(response.message || 'Failed to delete feedback');
        setShowErrorPopup(true);
      }
    } catch (err) {
      console.error('💥 FeedbackPage: Error deleting feedback:', err);
      setError('Network error while deleting feedback');
      setShowErrorPopup(true);
    }
  };

  // Handle withdraw application (creates new entry with withdrawn status)
  const handleWithdrawApplication = async (job) => {
    if (!window.confirm('Are you sure you want to withdraw this application?')) {
      return;
    }

    try {
      console.log('🗑️ FeedbackPage: Withdrawing application for job:', job.job_id);
      const response = await updateJobApplicationStatus(candidateId, job.job_id, 'withdrawn', 'Application withdrawn by candidate');
      console.log('📥 FeedbackPage: Withdraw response:', response);

      if (response.success) {
        console.log('✅ FeedbackPage: Application withdrawn successfully');
        setSuccessMessage('Application withdrawn successfully! 🗑️');
        setShowSuccessPopup(true);

        // Refresh job applications list
        const jobAppsResponse = await getCandidateJobApplications(candidateId);
        if (jobAppsResponse.success) {
          setJobApplications(jobAppsResponse.data || []);
        }
      } else {
        setError(response.message || 'Failed to withdraw application');
        setShowErrorPopup(true);
      }
    } catch (err) {
      console.error('💥 FeedbackPage: Error withdrawing application:', err);
      setError('Network error while withdrawing application');
      setShowErrorPopup(true);
    }
  };

  // Handle edit feedback
  const handleEditFeedback = (item) => {
    setEditingFeedbackId(item.FeedbackID);
    setEditFormData({
      comment: item.Comment,
      status: item.Status
    });
  };

  // Handle edit form change
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle edit form submit
  const handleEditFormSubmit = async (e) => {
    e.preventDefault();

    if (!editFormData.comment.trim()) {
      setError('Comment is required');
      setShowErrorPopup(true);
      return;
    }

    if (!editFormData.status) {
      setError('Status is required');
      setShowErrorPopup(true);
      return;
    }

    try {
      console.log('🚀 FeedbackPage: Updating feedback:', editingFeedbackId);
      const response = await updateFeedback(editingFeedbackId, editFormData);
      console.log('📥 FeedbackPage: Update response:', response);

      if (response.success) {
        console.log('✅ FeedbackPage: Feedback updated successfully');
        setSuccessMessage('Feedback updated successfully! ✏️');
        setShowSuccessPopup(true);

        // Refresh feedback list
        const updatedResponse = await getCandidateFeedback(candidateId);
        if (updatedResponse.success) {
          setFeedback(updatedResponse.data || []);
        }

        // Reset edit form
        setEditingFeedbackId(null);
        setEditFormData({ comment: "", status: "" });
      } else {
        setError(response.message || 'Failed to update feedback');
        setShowErrorPopup(true);
      }
    } catch (err) {
      console.error('💥 FeedbackPage: Error updating feedback:', err);
      setError('Network error while updating feedback');
      setShowErrorPopup(true);
    }
  };

  // Handle form submit
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!formData.comment.trim()) {
      setError('Comment is required');
      setShowErrorPopup(true);
      return;
    }

    if (!formData.status) {
      setError('Status is required');
      setShowErrorPopup(true);
      return;
    }

    try {
      setFormSubmitting(true);
      console.log('🚀 FeedbackPage: Submitting new feedback:', formData);

      const response = await createFeedback({
        candidateId: candidateId,
        comment: formData.comment,
        status: formData.status,
        createdBy: user?.EmployeeID || user?.Email || user?.username || 'System'
      });

      console.log('📥 FeedbackPage: Response received:', response);

      if (response.success) {
        console.log('✅ FeedbackPage: Feedback created successfully');
        setSuccessMessage('Feedback created successfully! 🎉');
        setShowSuccessPopup(true);

        // Refresh feedback list
        const updatedResponse = await getCandidateFeedback(candidateId);
        if (updatedResponse.success) {
          setFeedback(updatedResponse.data || []);
        }

        // Reset form
        setFormData({
          comment: "",
          status: "HR Round"
        });
        setShowForm(false);
      } else {
        setError(response.message || 'Failed to create feedback');
        setShowErrorPopup(true);
      }
    } catch (err) {
      console.error('💥 FeedbackPage: Error creating feedback:', err);
      setError('Network error while creating feedback');
      setShowErrorPopup(true);
    } finally {
      setFormSubmitting(false);
    }
  };

  // Handle sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Handle apply for job
  const handleApplyForJob = async () => {
    if (!selectedJobId) {
      setError('Please select a job from the list');
      setShowErrorPopup(true);
      return;
    }

    setApplyingForJob(true);
    try {
      const response = await applyForJob(candidateId, parseInt(selectedJobId));
      if (response.success) {
        // Find the applied job details
        const appliedJob = jobs.find(job => job.job_id === parseInt(selectedJobId));
        setAppliedJobDetails(appliedJob);
        setShowApplyJobPopup(false);
        setShowNotificationPopup(true);

        // Refresh job applications
        const jobAppsResponse = await getCandidateJobApplications(candidateId);
        if (jobAppsResponse.success) {
          setJobApplications(jobAppsResponse.data || []);
        }
      } else {
        // Check if it's an already applied error
        if (response.alreadyApplied) {
          setAlreadyAppliedJobName(response.jobName || 'this job');
          setShowApplyJobPopup(false);
          setShowAlreadyAppliedPopup(true);
        } else {
          setError(response.message || 'Failed to apply for job');
          setShowErrorPopup(true);
        }
      }
    } catch (err) {
      console.error('Error applying for job:', err);
      setError('Network error while applying for job');
      setShowErrorPopup(true);
    } finally {
      setApplyingForJob(false);
    }
  };

  // Handle notification actions
  const handleSendEmail = () => {
    if (!fullCandidateData?.email || !appliedJobDetails) return;
    const subject = encodeURIComponent(`Job Opportunity: ${appliedJobDetails.title} at ${appliedJobDetails.company}`);
    const body = encodeURIComponent(`Hi ${fullCandidateData.name},\n\nWe have a job opportunity that matches your profile. Please review the Job Description below and confirm whether you are comfortable with it and would like us to proceed with your application.\n\n📋 Job Details:\n* Position: ${appliedJobDetails.title}\n* Company: ${appliedJobDetails.company}\n* Location: ${appliedJobDetails.location}\n* Experience: ${appliedJobDetails.experience_required}\n* Department: ${appliedJobDetails.department}\n\nDescription:\n${appliedJobDetails.job_description || appliedJobDetails.description || 'N/A'}\n\nPlease let us know if you are comfortable with this JD and would like to proceed with the application.\n\nReply with:\nYes, I am comfortable with this JD and would like to proceed.\nNo, I am not interested in this opportunity.\n\nThank you,\nIntMavens Recruitment Team`);
    window.open(`mailto:${fullCandidateData.email}?subject=${subject}&body=${body}`);
    setShowNotificationPopup(false);
  };

  const handleSendEmailForJob = (job) => {
    if (!fullCandidateData?.email || !job) return;
    const subject = encodeURIComponent(`Job Opportunity: ${job.job_title || job.title} at ${job.job_company || job.company}`);
    const body = encodeURIComponent(`Hi ${fullCandidateData.name},\n\nWe have a job opportunity that matches your profile. Please review the Job Description below and confirm whether you are comfortable with it and would like us to proceed with your application.\n\n📋 Job Details:\n* Position: ${job.job_title || job.title}\n* Company: ${job.job_company || job.company}\n* Location: ${job.job_location || job.location}\n* Experience: ${job.experience_required}\n* Department: ${job.department}\n\nDescription:\n${job.job_description || job.description || 'N/A'}\n\nPlease let us know if you are comfortable with this JD and would like to proceed with the application.\n\nReply with:\nYes, I am comfortable with this JD and would like to proceed.\nNo, I am not interested in this opportunity.\n\nThank you,\nIntMavens Recruitment Team`);
    window.open(`mailto:${fullCandidateData.email}?subject=${subject}&body=${body}`);
  };

  const handleSendWhatsAppForJob = (job) => {
    if (!fullCandidateData?.phone || !job) return;

    const message = encodeURIComponent(`Hi ${fullCandidateData.name},\n\nWe have a job opportunity that matches your profile. Please review the Job Description below and confirm whether you are comfortable with it and would like us to proceed with your application.\n\n📋 Job Details:\n* Position: ${job.job_title || job.title}\n* Company: ${job.job_company || job.company}\n* Location: ${job.job_location || job.location}\n* Experience: ${job.experience_required}\n* Department: ${job.department}\n\nDescription:\n${job.job_description || job.description || 'N/A'}\n\nPlease let us know if you are comfortable with this JD and would like to proceed with the application.\n\nReply with:\nYes, I am comfortable with this JD and would like to proceed.\nNo, I am not interested in this opportunity.\n\nThank you,\nIntMavens Recruitment Team`);

    window.open(`https://wa.me/91${fullCandidateData.phone}?text=${message}`);
  };

  const WhatsAppIcon = () => (
    <img
      src="https://img.icons8.com/ios-filled/24/ffffff/whatsapp.png"
      alt="WhatsApp"
      style={{ width: '18px', height: '18px' }}
    />
  );

  const handleSendWhatsApp = () => {
    if (!fullCandidateData?.phone || !appliedJobDetails) return;

    const message = encodeURIComponent(`Hi ${fullCandidateData.name},\n\nWe have a job opportunity that matches your profile. Please review the Job Description below and confirm whether you are comfortable with it and would like us to proceed with your application.\n\n📋 Job Details:\n* Position: ${appliedJobDetails.title}\n* Company: ${appliedJobDetails.company}\n* Location: ${appliedJobDetails.location}\n* Experience: ${appliedJobDetails.experience_required}\n* Department: ${appliedJobDetails.department}\n\nDescription:\n${appliedJobDetails.job_description || appliedJobDetails.description || 'N/A'}\n\nPlease let us know if you are comfortable with this JD and would like to proceed with the application.\n\nReply with:\nYes, I am comfortable with this JD and would like to proceed.\nNo, I am not interested in this opportunity.\n\nThank you,\nIntMavens Recruitment Team`);

    window.open(`https://wa.me/91${fullCandidateData.phone}?text=${message}`);
    setShowNotificationPopup(false);
  };

  return (
    <div className="scrapper-container">
      <div className="scrapper-header">
      </div>

      {/* Candidate Info Card */}
      {fullCandidateData && (
        <div className="feedback-candidate-card">
          <div className="feedback-candidate-info">
            <div className="feedback-candidate-header">
              <div>
                <h3 className="feedback-candidate-name">{fullCandidateData.name}</h3>
                <p className="feedback-candidate-summary"><strong>Role:</strong> {fullCandidateData.role}</p>
                <p className="feedback-candidate-summary"><strong>Experience:</strong> {fullCandidateData.experience} years</p>
                <p className="feedback-candidate-summary"><strong>Location:</strong> {fullCandidateData.location}</p>
              </div>
              <button
                type="button"
                className="toggle-details-btn"
                onClick={() => setShowCandidateDetails((prev) => !prev)}
              >
                {showCandidateDetails ? 'Hide Candidate Info' : 'Show Candidate Info'}
              </button>
            </div>

            {showCandidateDetails && (
              <div className="feedback-candidate-details">
                <p><strong>Source:</strong> {fullCandidateData.source}</p>
                <p><strong>Email:</strong> {fullCandidateData.email}</p>
                <p><strong>Phone:</strong> {fullCandidateData.phone}</p>
                <p><strong>Aadhar No:</strong> {fullCandidateData.aadharNo}</p>
                <p><strong>Pan Card No:</strong> {fullCandidateData.pancardNo}</p>
                <p><strong>Primary Skills:</strong> {fullCandidateData.primarySkills}</p>
                <p><strong>Secondary Skills:</strong> {fullCandidateData.secondarySkills}</p>
                <p><strong>Tech Stack:</strong> {fullCandidateData.techStack}</p>
                <p><strong>Work Type:</strong> {fullCandidateData.workType}</p>
                <p><strong>Ready to Travel:</strong> {fullCandidateData.readyToTravel ? 'Yes' : 'No'}</p>
                <p><strong>Remote Available:</strong> {fullCandidateData.remoteAvailable ? 'Yes' : 'No'}</p>
                <p><strong>Notice Period:</strong> {fullCandidateData.noticePeriod}</p>
                <p><strong>CTC:</strong> {fullCandidateData.c2c || 'N/A'}</p>
                <p><strong>Expected CTC:</strong> {fullCandidateData.expectedCTC}</p>
                <p><strong>Freelance Rate:</strong> {fullCandidateData.freelanceRate}</p>
                <p><strong>Referrer Code:</strong> {fullCandidateData.referrerCode}</p>
                <p><strong>Applied Date:</strong> {fullCandidateData.appliedDate ? new Date(fullCandidateData.appliedDate).toLocaleDateString() : 'N/A'}</p>
                <p><strong>Available From:</strong> {fullCandidateData.availableFrom ? new Date(fullCandidateData.availableFrom).toLocaleDateString() : 'Not specified'}</p>
                <p><strong>Resume:</strong> {fullCandidateData.resumeUrl ? <a href={fullCandidateData.resumeUrl} target="_blank" rel="noopener noreferrer" className="download-link">Download</a> : 'No Resume'}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="feedback-action-section">
        {fullCandidateData && (
          <>
            <button
              className="scrapper-link-button"
              onClick={() => {
                navigate('/resume', {
                  state: {
                    candidateData: fullCandidateData,
                    isEditMode: true
                  }
                });
              }}
            >
              ✏️ Edit Candidate Details
            </button>
            <button
              className="scrapper-link-button"
              onClick={() => {
                navigate('/resume-update', {
                  state: {
                    candidateData: fullCandidateData,
                    candidateId: fullCandidateData.id || candidateId,
                    resumeId: fullCandidateData.resumeId || null,
                    isEditMode: true
                  }
                });
              }}
            >
              ✏️ Update Resume Details
            </button>
          </>
        )}
        <button
          className="scrapper-link-button"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? '✕ Close Form' : '➕Add New Feedback'}
        </button>
        <button
          className="scrapper-link-button"
          onClick={() => setShowApplyJobPopup(true)}
          style={{ backgroundColor: '#28a745' }}
        >
          💼 Apply for Job
        </button>
         <button className="scrapper-link-button" onClick={() => navigate('/jobs')}>
            View Jobs List
          </button>
      </div>

      {/* Apply for Job Popup */}
      {showApplyJobPopup && (
        <div className="popup-overlay" onClick={() => setShowApplyJobPopup(false)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>Apply for Job</h3>
              <button
                className="popup-close"
                onClick={() => {
                  setShowApplyJobPopup(false);
                  setJobSearch("");
                  setSelectedJobId(null);
                }}
              >
                ✕
              </button>
            </div>
            <div className="popup-body">
              <label htmlFor="jobSearch" className="popup-label">Search by Job ID or Title</label>
              <input
                id="jobSearch"
                type="text"
                value={jobSearch}
                onChange={(e) => setJobSearch(e.target.value)}
                placeholder="Type Job ID or Title..."
                className="popup-input"
                disabled={jobsLoading || applyingForJob}
              />
              <div className="popup-job-list">
                {jobsLoading ? (
                  <p className="popup-loading">Loading jobs...</p>
                ) : filteredJobs.length === 0 ? (
                  <p className="popup-no-results">No matching jobs found.</p>
                ) : (
                  <table className="popup-job-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Job ID</th>
                        <th>Title</th>
                        <th>Experience</th>
                        {/* <th>Department</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredJobs.map((job) => (
                        <tr
                          key={job.job_id}
                          className={selectedJobId === job.job_id ? 'popup-job-row selected' : 'popup-job-row'}
                          onClick={() => setSelectedJobId(job.job_id)}
                        >
                          <td className="popup-job-radio-cell">
                            <input
                              type="radio"
                              name="selectedJob"
                              checked={selectedJobId === job.job_id}
                              onChange={() => setSelectedJobId(job.job_id)}
                            />
                          </td>
                          <td>{job.job_id}</td>
                          <td>{job.title || 'N/A'}</td>
                          <td>{job.experience_required || 'N/A'}</td>
                          {/* <td>{job.department || 'N/A'}</td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            <div className="popup-footer">
              <button
                className="popup-btn popup-btn-primary"
                onClick={handleApplyForJob}
                disabled={applyingForJob || !selectedJobId}
              >
                {applyingForJob ? '⏳ Applying...' : 'Apply Job'}
              </button>

            </div>
          </div>
        </div>
      )}

      {/* Notification Popup after applying */}
      {showNotificationPopup && appliedJobDetails && (
        <div className="popup-overlay" onClick={() => setShowNotificationPopup(false)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>Application Submitted! 🎉</h3>
              <button
                className="popup-close"
                onClick={() => setShowNotificationPopup(false)}
              >
                ✕
              </button>
            </div>
            <div className="popup-body">
              <div className="popup-message">
                <div className="popup-title">🎉 Congratulations {fullCandidateData?.name || 'Candidate'}!</div>
                <div className="popup-text" style={{ whiteSpace: 'pre-line', marginTop: '10px' }}>
                  {`You have successfully applied for the ${appliedJobDetails.title} position at ${appliedJobDetails.company}.

📋 Job Details:
* Position: ${appliedJobDetails.title}
* Company: ${appliedJobDetails.company}
* Location: ${appliedJobDetails.location}
* Experience: ${appliedJobDetails.experience_required}
* Department: ${appliedJobDetails.department}

We will review your application and get back to you soon.

Best regards,
IntMavens Recruitment Team`}
                </div>
              </div>
            </div>
            <div className="popup-footer">
              <button
                className="popup-btn popup-btn-primary"
                onClick={handleSendEmail}
                disabled={emailSending}
                style={{ marginRight: '10px' }}
              >
                {emailSending ? 'Sending...' : '📧 Send Email'}
              </button>
              <button
                className="popup-btn popup-btn-secondary"
                onClick={handleSendWhatsApp}
                style={{ backgroundColor: '#25D366', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 12px' }}
              >
                <WhatsAppIcon />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Already Applied Popup */}
      {showAlreadyAppliedPopup && (
        <div className="popup-overlay" onClick={() => setShowAlreadyAppliedPopup(false)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>Already Applied ⚠️</h3>
              <button
                className="popup-close"
                onClick={() => setShowAlreadyAppliedPopup(false)}
              >
                ✕
              </button>
            </div>
            <div className="popup-body">
              <div className="popup-message">
                <div className="popup-text">
                  You have already applied for <strong>{alreadyAppliedJobName}</strong>
                </div>
                <div className="popup-text" style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                  If you previously withdrew this application, you can apply again.
                </div>
              </div>
            </div>
            <div className="popup-footer">
              <button
                className="popup-btn popup-btn-secondary"
                onClick={() => setShowAlreadyAppliedPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Applied Jobs Table */}
      <div className="scrapper-table-wrapper scrapper-table-wrapper-scroll">
        {/* <div className="section-header">
          <div>
            <p className="section-description">This section shows the jobs applied by the candidate. Use the filters to find a job quickly.</p>
          </div>
          <button className="scrapper-link-button" onClick={() => navigate('/jobs')}>
            View Jobs List
          </button>
        </div> */}

        {/* <div className="feedback-table-toolbar">
          <input
            type="text"
            className="scrapper-input"
            placeholder="Filter Job ID, Title, Company..."
            value={jobApplicationFilter}
            onChange={(e) => setJobApplicationFilter(e.target.value)}
          />
          <select
            className="scrapper-input"
            value={jobApplicationStatus}
            onChange={(e) => setJobApplicationStatus(e.target.value)}
          >
            <option value="">All Statuses</option>
            {Array.from(new Set(jobApplications.map(job => job.status).filter(Boolean))).map(status => (
              <option key={status} value={status.toLowerCase()}>{status}</option>
            ))}
          </select>
          <button
            className="scrapper-filter-btn reset-btn"
            type="button"
            onClick={() => {
              setJobApplicationFilter('');
              setJobApplicationStatus('');
            }}
          >
            Reset
          </button>
        </div> */}

        <table className="scrapper-table feedback-table scrapper-table-collapse">
          <thead>
            <tr>
              {APPLIED_JOBS_TABLE_COLUMNS.map(column => (
                <th
                  key={column.key}
                  className={`scrapper-th ${column.className || ''} ${column.isAction ? 'scrapper-th-actions' : ''}`}
                  style={{
                    width: column.width,
                    minWidth: column.minWidth || column.width,
                    maxWidth: column.maxWidth || 'none'
                  }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredJobApplications.length > 0 ? (
              filteredJobApplications.map((job, idx) => (
                <tr key={`${job.mapping_id || job.job_id}-${idx}`} className="scrapper-tr-hover">
                  {APPLIED_JOBS_TABLE_COLUMNS.map((column) => {
                    const baseStyle = {
                      width: column.width,
                      minWidth: column.minWidth || column.width,
                      maxWidth: column.maxWidth || 'none'
                    };

                    if (column.key === 'srNo') {
                      return (
                        <td key={column.key} className={`scrapper-td ${column.className || ''}`} style={baseStyle}>
                          {idx + 1}
                        </td>
                      );
                    }

                    if (column.key === 'job_title') {
                      return (
                        <td key={column.key} className={`scrapper-td ${column.className || ''}`} style={baseStyle}>
                          <button
                            onClick={() => {
                              navigate('/job-detail', {
                                state: {
                                  jobId: job.job_id,
                                  candidateId,
                                  job: job
                                }
                              });
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#007bff',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              padding: 0,
                              fontSize: 'inherit',
                              fontWeight: 'bold'
                            }}
                            title="Click to view job details"
                          >
                            {job.job_title || job.title || 'N/A'}
                          </button>
                        </td>
                      );
                    }

                    if (column.key === 'remote_work') {
                      return (
                        <td key={column.key} className={`scrapper-td ${column.className || ''}`} style={baseStyle}>
                          <span className="scrapper-status-badge">{job.remote_work ? 'Yes' : 'No'}</span>
                        </td>
                      );
                    }

                    if (column.key === 'budget') {
                      return (
                        <td key={column.key} className={`scrapper-td ${column.className || ''}`} style={baseStyle}>
                          {job.budget ? `₹${parseInt(job.budget).toLocaleString()}` : 'N/A'}
                        </td>
                      );
                    }

                    if (column.key === 'applied_on') {
                      return (
                        <td key={column.key} className={`scrapper-td ${column.className || ''}`} style={baseStyle}>
                          {job.created_at ? new Date(job.created_at).toLocaleDateString('en-IN') : 'N/A'}
                        </td>
                      );
                    }

                    if (column.key === 'status') {
                      return (
                        <td key={column.key} className={`scrapper-td ${column.className || ''}`} style={baseStyle}>
                          <span className={`scrapper-status-badge scrapper-status-${String(job.status || 'applied').toLowerCase()}`}>
                            {job.status ? job.status.charAt(0).toUpperCase() + job.status.slice(1) : 'Applied'}
                          </span>
                        </td>
                      );
                    }

                    if (column.key === 'notice') {
                      return (
                        <td
                          key={column.key}
                          className={`scrapper-td ${column.className || ''}`}
                          style={{
                            ...baseStyle,
                            width: '40px',
                            minWidth: '40px',
                            maxWidth: '40px',
                            border: 'none',
                            padding: '2px'
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                            <button
                              type="button"
                              className="popup-btn popup-btn-primary"
                              onClick={() => handleSendEmailForJob(job)}
                              disabled={emailSending || !fullCandidateData?.email}
                              style={{
                                width: '100%',
                                minWidth: '0',
                                padding: '4px',
                                fontSize: '12px',
                                border: 'none'
                              }}
                            >
                              {emailSending ? '✉️' : '📧'}
                            </button>
                            <button
                              type="button"
                              className="popup-btn popup-btn-secondary"
                              onClick={() => handleSendWhatsAppForJob(job)}
                              disabled={!fullCandidateData?.phone}
                              style={{
                                width: '100%',
                                minWidth: '0',
                                padding: '4px',
                                fontSize: '12px',
                                backgroundColor: '#25D366',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: 'none'
                              }}
                            >
                              <WhatsAppIcon />
                            </button>
                          </div>
                        </td>
                      );
                    }

                    return (
                      <td key={column.key} className={`scrapper-td ${column.className || ''}`} style={baseStyle}>
                        {job[column.key] || 'N/A'}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={APPLIED_JOBS_TABLE_COLUMNS.length} className="scrapper-no-data">
                  No job applications found. Click "Apply for Job" to apply for positions.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Feedback Form */}
      {showForm && (
        <div className="feedback-form-container">
          <h3 className="feedback-form-title">Create New Feedback</h3>
          <form onSubmit={handleFormSubmit} className="feedback-form">
            <div className="feedback-form-group">
              <label className="feedback-form-label">Comment:</label>
              <textarea
                name="comment"
                value={formData.comment}
                onChange={handleFormChange}
                placeholder="Enter your feedback comment..."
                className="feedback-form-textarea"
                rows="5"
                required
              />
            </div>

            <div className="feedback-form-group">
              <label className="feedback-form-label">Status:</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleFormChange}
                className="feedback-form-select"
              >
                {feedbackStatusOptions.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            <div className="feedback-form-actions">
              <button
                type="submit"
                className="feedback-btn-submit"
                disabled={formSubmitting}
              >
                {formSubmitting ? '⏳ Submitting...' : '✓ Submit Feedback'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="scrapper-filters">
        <div className="scrapper-filter-group">
          <label className="scrapper-filter-label">Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="scrapper-filter-select"
          >
            <option value="">All Statuses</option>
            {candidateStatusOptions.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-filter-label">Start Date:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="scrapper-filter-input"
          />
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-filter-label">End Date:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="scrapper-filter-input"
          />
        </div>
      </div>

      {/* Feedback Table */}
      <div className="scrapper-table-container feedback-section">
        <table className="scrapper-table">
          <thead>
            <tr>
              {FEEDBACK_TABLE_COLUMNS.map(column => (
                <th
                  key={column.key}
                  className={`scrapper-th ${column.className || ''} ${column.isSortable ? 'scrapper-th-sortable' : ''}`}
                  onClick={() => column.isSortable && handleSort(column.key)}
                  style={{
                    width: column.width,
                    minWidth: column.width,
                    maxWidth: column.width,
                    cursor: column.isSortable ? 'pointer' : 'default'
                  }}
                >
                  {column.label}
                  {column.isSortable && (
                    <>
                      {sortConfig.key === column.key ? (
                        <span className="scrapper-sort-indicator">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      ) : (
                        <span className="scrapper-sort-unsorted">↕</span>
                      )}
                    </>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={FEEDBACK_TABLE_COLUMNS.length} className="scrapper-no-data scrapper-loading-cell">
                  <div className="scrapper-loading-container">
                    <span className="scrapper-spinner-loader"></span>
                    Loading feedback...
                  </div>
                </td>
              </tr>
            ) : filteredFeedback.length === 0 ? (
              <tr>
                <td colSpan={FEEDBACK_TABLE_COLUMNS.length} className="scrapper-no-data">
                  {feedback.length === 0 ? 'No feedback found. Create one!' : 'No feedback matches the current filters.'}
                </td>
              </tr>
            ) : (
              filteredFeedback.map((item, idx) => (
                <tr key={`${item.FeedbackID}-${idx}`} className="scrapper-tr-hover">
                  {FEEDBACK_TABLE_COLUMNS.map((column) => {
                    const baseStyle = {
                      width: column.width,
                      minWidth: column.width,
                      maxWidth: column.width
                    };

                    if (column.key === 'srNo') {
                      return (
                        <td key={column.key} className={`scrapper-td ${column.className || ''}`} style={baseStyle}>
                          {idx + 1}
                        </td>
                      );
                    }

                    if (column.key === 'status') {
                      return (
                        <td key={column.key} className={`scrapper-td ${column.className || ''}`} style={baseStyle}>
                          <span className={`scrapper-status-badge scrapper-status-feedback`}>
                            {item.Status || 'Resume Review'}
                          </span>
                        </td>
                      );
                    }

                    if (column.key === 'comment') {
                      return (
                        <td key={column.key} className={`scrapper-td scrapper-cell-wrap ${column.className || ''}`} style={baseStyle}>
                          {item.Comment || 'No comment'}
                        </td>
                      );
                    }

                    if (column.key === 'createdBy') {
                      return (
                        <td key={column.key} className={`scrapper-td ${column.className || ''}`} style={baseStyle}>
                          {item.CreatedBy || 'N/A'}
                        </td>
                      );
                    }

                    if (column.key === 'createdDate') {
                      return (
                        <td key={column.key} className={`scrapper-td ${column.className || ''}`} style={baseStyle}>
                          {item.CreatedDate ? new Date(item.CreatedDate).toLocaleDateString() : 'N/A'}
                        </td>
                      );
                    }

                    return (
                      <td key={column.key} className={`scrapper-td ${column.className || ''}`} style={baseStyle}>
                        {item[column.key] || 'N/A'}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
            {/* Edit Feedback Row */}
            {editingFeedbackId && (
              <tr className="scrapper-edit-row">
                <td colSpan={FEEDBACK_TABLE_COLUMNS.length} className="scrapper-edit-cell">
                  <div className="feedback-edit-form">
                    <h4>Edit Feedback</h4>
                    <form onSubmit={handleEditFormSubmit} className="feedback-form-inline">
                      <div className="feedback-form-row">
                        <div className="feedback-form-group" style={{ flex: 2 }}>
                          <label>Comment:</label>
                          <textarea
                            name="comment"
                            value={editFormData.comment}
                            onChange={handleEditFormChange}
                            className="feedback-form-textarea"
                            rows="3"
                            required
                          />
                        </div>
                        <div className="feedback-form-group" style={{ flex: 1 }}>
                          <label>Status:</label>
                          <select
                            name="status"
                            value={editFormData.status}
                            onChange={handleEditFormChange}
                            className="feedback-form-select"
                            required
                          >
                            <option value="">-- Select Status --</option>
                            {feedbackStatusOptions.map(status => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="feedback-edit-actions">
                        <button type="submit" className="feedback-btn-submit">✓ Save</button>
                        <button
                          type="button"
                          className="feedback-btn-cancel"
                          onClick={() => {
                            setEditingFeedbackId(null);
                            setEditFormData({ comment: "", status: "" });
                          }}
                        >
                          ✕ Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Error Popup */}
      <PopupMessage
        show={showErrorPopup}
        message={error}
        type="error"
        onClose={() => setShowErrorPopup(false)}
      />

      {/* Success Popup */}
      <PopupMessage
        show={showSuccessPopup}
        message={successMessage}
        type="success"
        onClose={() => setShowSuccessPopup(false)}
      />
    </div>
  );
};

export default FeedbackPage;